import React from 'react'
import { Link } from 'react-router-dom'
import type { CSSProperties } from 'react'

interface ButtonProps {
  variant?: 'primary' | 'ghost' | 'bare'
  size?: 'sm' | 'md' | 'lg'
  type?: 'button' | 'submit'
  disabled?: boolean
  onClick?: () => void
  /** When set, the button renders as a react-router <Link> (real href) with identical styling. */
  to?: string
  children: React.ReactNode
  className?: string
}

const SIZES = {
  sm: { h: 'h-9', text: 'text-[13px]', px: 'px-4' },
  md: { h: 'h-12', text: 'text-[14.5px]', px: 'px-5' },
  lg: { h: 'h-14', text: 'text-[15px]', px: 'px-7' },
} as const

export function Button({
  variant = 'primary',
  size = 'md',
  type = 'button',
  disabled,
  onClick,
  to,
  children,
  className = '',
}: ButtonProps) {
  const { h, text, px } = SIZES[size]
  const base = `inline-flex items-center justify-center gap-1.5 rounded-full font-semibold font-sans cursor-pointer transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed ${h} ${text} ${className}`

  let cls: string
  let style: CSSProperties | undefined

  if (variant === 'primary') {
    cls = `${base} ${px} border-0 text-white`
    style = {
      background: disabled ? 'var(--muted)' : 'linear-gradient(135deg, #3B4CCA, #7C5CD3)',
      boxShadow: disabled ? 'none' : '0 4px 14px rgba(59,76,202,.25)',
    }
  } else if (variant === 'ghost') {
    cls = `${base} ${px} bg-transparent border-[1.5px] border-line text-ink-2 hover:border-ink-2`
  } else {
    // bare — minimal chrome, no border, no background
    cls = `${base} px-2 border-0 bg-transparent text-ink-2 hover:text-ink`
  }

  if (to && !disabled) {
    return (
      <Link to={to} onClick={onClick} className={cls} style={style}>
        {children}
      </Link>
    )
  }

  return (
    <button type={type} disabled={disabled} onClick={onClick} className={cls} style={style}>
      {children}
    </button>
  )
}
